import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function WritingPage() {
  useEffect(() => {
    document.title = 'Writing & Insights | Henry Adebisi';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Articles and thought leadership on finance strategy, fintech innovation, and building sustainable African enterprises that compete globally.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/writing`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'thought leadership, finance strategy, fintech innovation, African enterprises, business insights');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Writing & Insights | Henry Adebisi",
      "description": "Articles and thought leadership on finance strategy, fintech innovation, and building sustainable African enterprises that compete globally.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/writing`,
      "author": {
        "@type": "Person",
        "name": "Henry Olamide Adebisi"
      },
      "mainEntity": [
        {
          "@type": "Article",
          "headline": "Rethinking Africa's Investment Narrative",
          "author": {
            "@type": "Person",
            "name": "Henry Olamide Adebisi"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BusinessDay"
          },
          "url": "https://businessday.ng/opinion/article/rethinking-africas-investment-narrative-why-real-businesses-remain-invisible-to-capital-markets/#"
        },
        {
          "@type": "Article",
          "headline": "How African Entrepreneurs Scale Under Constraints",
          "author": {
            "@type": "Person",
            "name": "Henry Olamide Adebisi"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Business Insider Africa"
          },
          "url": "https://africa.businessinsider.com/local/leaders/how-african-entrepreneurs-scale-under-constraints-and-why-investors-should-pay/5k499m5"
        }
      ]
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const articles = [
    {
      title: 'Rethinking Africa\'s Investment Narrative',
      publication: 'BusinessDay',
      excerpt: 'Why real businesses remain invisible to capital markets and how we can change the narrative around African investment opportunities.',
      url: 'https://businessday.ng/opinion/article/rethinking-africas-investment-narrative-why-real-businesses-remain-invisible-to-capital-markets/#',
      date: '2024',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/3cd671a0a1d3755f506cadb5fd24e78e.jpg'
    },
    {
      title: 'How African Entrepreneurs Scale Under Constraints',
      publication: 'Business Insider Africa',
      excerpt: 'Exploring the unique challenges African entrepreneurs face and why investors should pay attention to their innovative solutions.',
      url: 'https://africa.businessinsider.com/local/leaders/how-african-entrepreneurs-scale-under-constraints-and-why-investors-should-pay/5k499m5',
      date: '2024',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=African%20entrepreneurs%20working%20in%20modern%20startup%20environment%2C%20innovative%20business%20solutions%2C%20technology%20and%20finance%2C%20collaborative%20workspace%2C%20entrepreneurial%20spirit&width=400&height=250&seq=article2&orientation=landscape'
    },
    {
      title: 'Scaling Finance Operations Across Borders: Lessons from African Markets',
      publication: 'LinkedIn Pulse',
      excerpt: 'Practical insights on building robust financial systems that work across multiple African markets and regulatory environments.',
      url: 'https://www.linkedin.com/pulse/scaling-finance-operations-across-borders-lessons-african-adebisi-oebrf/',
      date: '2024',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=Cross-border%20finance%20operations%20in%20Africa%2C%20multiple%20country%20flags%2C%20financial%20systems%20integration%2C%20modern%20banking%20technology%2C%20international%20business%20operations&width=400&height=250&seq=article3&orientation=landscape'
    }
  ];

  const topics = [
    'Strategic Finance',
    'Fintech Innovation',
    'African Markets',
    'Investment Frameworks',
    'SME Growth',
    'Cross-border Operations',
    'Financial Transformation',
    'Sustainable Business'
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-sora font-bold text-4xl lg:text-5xl text-navy mb-6">
              Thought Leadership & Insights
            </h1>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Exploring the intersection of finance, technology, and sustainable growth across African markets. 
              Sharing insights on strategic finance, fintech innovation, and building scalable business systems.
            </p>
          </div>
        </section>

        {/* Topics Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-sora font-semibold text-xl text-navy mb-6 text-center">
              Key Topics
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {topics.map((topic, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-white text-gray-700 rounded-full text-sm font-medium shadow-sm"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-sora font-bold text-3xl text-navy mb-12 text-center">
              Featured Articles
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <article key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gold font-semibold text-sm">
                        {article.publication}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {article.readTime}
                      </span>
                    </div>
                    
                    <h3 className="font-sora font-bold text-xl text-navy mb-3 leading-tight">
                      {article.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">
                        {article.date}
                      </span>
                      <a 
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-navy font-semibold text-sm transition-colors duration-200"
                      >
                        Read Article <i className="ri-external-link-line ml-1"></i>
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl text-navy mb-6">
              Stay Updated
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Get notified when Henry publishes new insights on African finance, fintech innovation, and strategic business growth.
            </p>
            
            <div className="max-w-md mx-auto">
              <div className="flex gap-3">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
                <Button variant="secondary">
                  Subscribe
                </Button>
              </div>
              <p className="text-gray-500 text-sm mt-3">
                No spam. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-6">
              Collaborate on Content
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Interested in co-authoring articles, podcast appearances, or contributing to publications? 
              Let's explore collaboration opportunities.
            </p>
            <Button variant="secondary" size="lg" href="/contact">
              Discuss Collaboration
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
